import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { KeyRound, Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", {
        email: email.trim().toLowerCase(),
        origin_url: window.location.origin,
      });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("forgot.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] flex items-center justify-center p-6 bg-slate-50/50" data-testid="forgot-password-page">
      <div className="w-full max-w-md mec-card p-8">
        <Link to="/login" className="inline-flex items-center text-xs text-slate-500 hover:text-slate-900 mb-5">
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> {t("forgot.back_to_login")}
        </Link>
        <div className="flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-md bg-slate-900 flex items-center justify-center">
            <KeyRound className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-display font-bold text-slate-900">{t("forgot.title")}</div>
            <div className="text-xs text-slate-500">{t("forgot.subtitle")}</div>
          </div>
        </div>

        {sent ? (
          <div className="text-center py-6" data-testid="forgot-sent">
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <h3 className="font-display text-lg font-bold text-slate-900 mb-2">{t("forgot.sent_title")}</h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              {t("forgot.sent_desc")}
            </p>
            <p className="text-xs text-slate-500 mt-4 leading-relaxed">{t("forgot.spam_hint")}</p>
            <Link to="/login" className="inline-block mt-6 text-sm text-[#004EEB] hover:underline font-medium">
              {t("forgot.back_to_login")}
            </Link>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-600 leading-relaxed mb-5">
              {t("forgot.intro")}
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="forgot-email" className="text-sm flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" /> {t("auth.email")}
                </Label>
                <Input
                  id="forgot-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  placeholder="vous@example.com"
                  data-testid="forgot-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#004EEB] hover:bg-[#003BCC] text-white"
                data-testid="forgot-submit"
              >
                {loading ? t("forgot.sending") : t("forgot.send_button")}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
